import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import type { EntitySource } from '@/shared/form/entity-source';
import { formatCivilDate, isCivilDate, type CivilDate } from '@/shared/format/date';
import { formatDocument } from '@/shared/format/document';
import {
  choiceLabel,
  enumValues,
  type ChoiceOption,
  type EnumDef,
} from '@/shared/patterns/define-enum';
import type { PatternText } from '@/shared/patterns/text';

import { endOfDayParam, startOfDayParam, moneyCentsParam } from './dates';

interface FilterBase {
  /** Chave na URL e, sem `param`, o nome do parâmetro no fio (U02): `name`, `initialDate`, `status`. */
  readonly key: string;
  readonly label: PatternText;
  readonly placeholder?: PatternText;
  /** Ganha um botão próprio na barra (web), além do painel "Filtros". */
  readonly pinned?: boolean;
  /** Oculto no editor; continua na URL, no fio e nos chips. */
  readonly hidden?: boolean;
  /** Nome do parâmetro no fio, quando difere da chave da URL. */
  readonly param?: string;
}

/** Texto livre. `search` é a busca "Todos" da barra: fica visível, fora do painel e dos chips. */
export interface TextFilter extends FilterBase {
  readonly type: 'text' | 'search';
  readonly max?: number;
  readonly toParam?: (value: string) => unknown;
}

/** CPF ou CNPJ com máscara progressiva. A URL guarda o cru; o fio decide o formato (U21) no `toParam`. */
export interface DocumentFilter extends FilterBase {
  readonly type: 'document';
  readonly toParam?: (raw: string) => unknown;
}

/** Uma opção entre várias (select do legado, sim/não). */
export interface SelectFilter extends FilterBase {
  readonly type: 'select';
  readonly options: EnumDef | readonly ChoiceOption[];
  readonly toParam?: (value: string) => unknown;
}

/** Várias opções; o fio repete a chave (`status=A&status=B`, `qs` repeat). `status` desenha tom e ícone (U19). */
export interface MultiFilter extends FilterBase {
  readonly type: 'multiselect' | 'status';
  readonly options: EnumDef;
  readonly toParam?: (values: readonly string[]) => unknown;
}

/** Entidade por busca remota (P06): produto, tomador, fundo… `multiple` acumula ids. */
export interface EntityFilter extends FilterBase {
  readonly type: 'entity';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- a lista mistura fontes de itens diferentes
  readonly source: EntitySource<any>;
  readonly multiple?: boolean;
  readonly toParam?: (value: string | readonly string[]) => unknown;
}

/** Um dia. A URL guarda a data civil (`2026-09-28`); o fio, o que `toParam` devolver (padrão: o dia). */
export interface DateFilter extends FilterBase {
  readonly type: 'date';
  readonly toParam?: (day: CivilDate) => unknown;
}

/**
 * Período: um campo, duas chaves (`key` = início, `to` = fim). O fim nunca some do fio por descuido
 * de configuração (o defeito do legado em Cessionários, U20-1): só sai com `sendTo: false`, que a tela
 * liga à constante do módulo enquanto a CreditAPI não confirma.
 */
export interface DateRangeFilter extends FilterBase {
  readonly type: 'dateRange';
  readonly to: string;
  readonly toParamName?: string;
  readonly fromLabel?: PatternText;
  readonly toLabel?: PatternText;
  /** Padrão: início do dia em -03:00 (`formatInitialDateWithTimeZone` do legado). */
  readonly fromParam?: (day: CivilDate) => unknown;
  /** Padrão: fim do dia em -03:00 (`formatFinalDateWithTimeZone` do legado). */
  readonly toParam?: (day: CivilDate) => unknown;
  /** Manda o fim ao fio (padrão `true`). U20-1: Cessionários passa `FUND_LIST_SENDS_FINAL_DATE`. */
  readonly sendTo?: boolean;
}

/** Faixa monetária em duas chaves; valores da URL são os textos digitados em reais. */
export interface MoneyRangeFilter extends FilterBase {
  readonly type: 'moneyRange';
  readonly to: string;
  readonly toParamName?: string;
  readonly fromLabel?: PatternText;
  readonly toLabel?: PatternText;
  readonly fromParam?: (value: string) => unknown;
  readonly toParam?: (value: string) => unknown;
  readonly sendTo?: boolean;
}

export type FilterField =
  | TextFilter
  | DocumentFilter
  | SelectFilter
  | MultiFilter
  | EntityFilter
  | DateFilter
  | DateRangeFilter
  | MoneyRangeFilter;
export type FilterType = FilterField['type'];

type FieldKeys<F> = F extends {
  readonly type: 'dateRange' | 'moneyRange';
  readonly key: infer K extends string;
  readonly to: infer T extends string;
}
  ? K | T
  : F extends { readonly key: infer K extends string }
    ? K
    : never;

type FieldValue<F> = F extends { readonly type: 'multiselect' | 'status' }
  ? string[]
  : F extends { readonly type: 'entity'; readonly multiple: true }
    ? string[]
    : string;

type ValueAt<Fs extends readonly FilterField[], K> = Fs[number] extends infer F
  ? F extends FilterField
    ? K extends FieldKeys<F>
      ? FieldValue<F>
      : never
    : never
  : never;

type UrlField<V> = z.ZodCatch<z.ZodOptional<z.ZodType<V>>>;

/** Pedaço do `validateSearch` gerado pelos filtros: `listSearchSchema({ ...set.shape, item: … })`. */
export type FilterShape<Fs extends readonly FilterField[]> = {
  [K in FieldKeys<Fs[number]>]: UrlField<ValueAt<Fs, K>>;
};

/** Valores dos filtros como estão na URL (civil, cru, ids). */
export type FilterValues<Fs extends readonly FilterField[]> = {
  [K in FieldKeys<Fs[number]>]?: ValueAt<Fs, K> | undefined;
};

/** Um filtro aplicado, para o chip ("Nome: FIDC"). */
export interface AppliedFilter {
  readonly field: FilterField;
  /** Valores na URL: um (texto, escolha, dia), vários (multi, entidade múltipla) ou `[início, fim]` do período. */
  readonly values: readonly string[];
  /** Patch da URL que tira o filtro (e volta à primeira página). */
  readonly clear: Readonly<Record<string, undefined>>;
}

export interface FilterSet<Fs extends readonly FilterField[] = readonly FilterField[]> {
  readonly fields: Fs;
  /** Pedaço do `validateSearch` (U07): tudo opcional, valor inválido cai fora em vez de quebrar a tela. */
  readonly shape: FilterShape<Fs>;
  /** Schema JSON-serializável do input da ação de filtrar (agente e página, U40). */
  readonly inputSchema: z.ZodType<FilterValues<Fs>>;
  /** Todas as chaves de URL dos filtros. */
  readonly keys: readonly string[];
  /** Só os valores dos filtros, sem vazios. */
  values(search: Readonly<Record<string, unknown>>): FilterValues<Fs>;
  /** Parâmetros do fio, na ordem da URL (a ordem em que a pessoa aplicou, como o legado). */
  toParams(search: Readonly<Record<string, unknown>>): Record<string, unknown>;
  /** Filtros aplicados, na ordem da declaração (a busca `search` fica de fora: já aparece no campo). */
  applied(search: Readonly<Record<string, unknown>>): AppliedFilter[];
  /** Quantos filtros do painel estão aplicados (o número do botão "Filtros"). */
  count(search: Readonly<Record<string, unknown>>): number;
  /** Há qualquer filtro, inclusive a busca: o vazio vira "nenhum resultado com esses filtros" (U39 §9). */
  isActive(search: Readonly<Record<string, unknown>>): boolean;
  /** Patch da URL com os valores novos: o resto dos filtros sai e a página volta para a primeira. */
  patch(next: Readonly<Record<string, unknown>>): Record<string, unknown>;
  /** Patch que limpa tudo ("Limpar filtros"). */
  clearPatch(): Record<string, undefined>;
  /** Subconjunto (modo "Excluídos" da Crédito, filtro só para tenant raiz). */
  pick(...keys: readonly string[]): FilterSet<Fs>;
  /** O campo que dona a chave (o período responde pelas duas). */
  fieldOf(key: string): FilterField | undefined;
}

const MAX_TEXT = 200;
const MAX_MULTI = 50;
const CIVIL = /^\d{4}-\d{2}-\d{2}$/;

const urlText = (schema: z.ZodType<string>) =>
  z.preprocess(
    (value) => (typeof value === 'number' && Number.isFinite(value) ? String(value) : value),
    schema,
  );
const urlList = (value: unknown) =>
  (Array.isArray(value) ? (value as unknown[]) : [value]).map((item) =>
    typeof item === 'number' && Number.isFinite(item) ? String(item) : item,
  );
const optionalCatch = <V>(schema: z.ZodType<V>): UrlField<V> => schema.optional().catch(undefined);
const blank = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);
const asList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item !== '')
    : typeof value === 'string' && value !== ''
      ? [value]
      : [];
function urlSchemas(field: FilterField): Record<string, z.ZodType> {
  switch (field.type) {
    case 'text':
    case 'search':
      return {
        [field.key]: optionalCatch(
          urlText(
            z
              .string()
              .trim()
              .min(1)
              .max(field.max ?? MAX_TEXT),
          ),
        ),
      };
    case 'document':
      return { [field.key]: optionalCatch(urlText(z.string().regex(/^[A-Za-z0-9]{1,14}$/))) };
    case 'select':
      return {
        [field.key]: optionalCatch(
          urlText(z.enum(enumValues(field.options) as [string, ...string[]])),
        ),
      };
    case 'multiselect':
    case 'status': {
      const item = z.enum(field.options.values as [string, ...string[]]);
      return {
        [field.key]: optionalCatch(z.preprocess(urlList, z.array(item).min(1).max(MAX_MULTI))),
      };
    }
    case 'entity':
      return {
        [field.key]: field.multiple
          ? optionalCatch(
              z.preprocess(urlList, z.array(z.string().min(1).max(100)).min(1).max(MAX_MULTI)),
            )
          : optionalCatch(urlText(z.string().min(1).max(100))),
      };
    case 'date':
      return { [field.key]: optionalCatch(z.string().regex(CIVIL).refine(isCivilDate)) };
    case 'moneyRange': {
      const amount = urlText(z.string().max(20));
      return { [field.key]: optionalCatch(amount), [field.to]: optionalCatch(amount) };
    }
    case 'dateRange': {
      const day = z.string().regex(CIVIL).refine(isCivilDate);
      return { [field.key]: optionalCatch(day), [field.to]: optionalCatch(day) };
    }
  }
}

function inputSchemas(field: FilterField): Record<string, z.ZodType> {
  // eslint-disable-next-line lingui/no-unlocalized-strings -- descrição do input para o agente, em inglês (U33)
  const day = z.string().regex(CIVIL).describe('Civil date, YYYY-MM-DD');
  switch (field.type) {
    case 'text':
    case 'search':
      return {
        [field.key]: z
          .string()
          .max(field.max ?? MAX_TEXT)
          .optional(),
      };
    case 'document':
      return {
        [field.key]: z
          .string()
          .regex(/^[A-Za-z0-9]{1,14}$/)
          // eslint-disable-next-line lingui/no-unlocalized-strings -- descrição canônica do input do agente (U33)
          .describe('CPF or CNPJ, digits and letters only')
          .optional(),
      };
    case 'select':
      return { [field.key]: z.enum(enumValues(field.options) as [string, ...string[]]).optional() };
    case 'multiselect':
    case 'status':
      return {
        [field.key]: z
          .array(z.enum(field.options.values as [string, ...string[]]))
          .max(MAX_MULTI)
          .optional(),
      };
    case 'entity':
      return {
        [field.key]: field.multiple
          ? z.array(z.string().min(1)).max(MAX_MULTI).optional()
          : z.string().min(1).optional(),
      };
    case 'date':
      return { [field.key]: day.optional() };
    case 'dateRange':
      return { [field.key]: day.optional(), [field.to]: day.optional() };
    case 'moneyRange':
      return {
        [field.key]: z.string().max(20).optional(),
        [field.to]: z.string().max(20).optional(),
      };
  }
}

function keysOf(field: FilterField): string[] {
  return field.type === 'dateRange' || field.type === 'moneyRange'
    ? [field.key, field.to]
    : [field.key];
}

function wireOf(
  field: Exclude<FilterField, DateRangeFilter | MoneyRangeFilter>,
  value: unknown,
): unknown {
  switch (field.type) {
    case 'text':
    case 'search':
    case 'select':
    case 'document':
      return typeof value === 'string' ? (field.toParam ? field.toParam(value) : value) : undefined;
    case 'date':
      return typeof value === 'string' ? (field.toParam ? field.toParam(value) : value) : undefined;
    case 'multiselect':
    case 'status': {
      const list = asList(value);
      return field.toParam ? field.toParam(list) : list;
    }
    case 'entity': {
      const list = asList(value);
      const current = field.multiple ? list : list[0];
      if (current === undefined) return undefined;
      return field.toParam ? field.toParam(current) : current;
    }
  }
}

/** Texto do valor de um filtro aplicado (sem o rótulo), para o chip. A entidade resolve o nome no componente. */
export function appliedText(i18n: I18n, applied: AppliedFilter): string {
  const { field, values } = applied;
  const day = (value: string | undefined) =>
    value ? (formatCivilDate(value, { locale: i18n.locale }) ?? value) : '';
  switch (field.type) {
    case 'dateRange': {
      const [from, to] = values;
      if (from && to) return `${day(from)} – ${day(to)}`;
      const fromText = day(from);
      const toText = day(to);
      return from ? i18n._(msg`a partir de ${fromText}`) : i18n._(msg`até ${toText}`);
    }
    case 'date':
      return day(values[0]);
    case 'document':
      return values[0] ? (formatDocument(values[0]) ?? values[0]) : '';
    case 'select':
      return values[0] ? choiceLabel(i18n, field.options, values[0]) : '';
    case 'multiselect':
    case 'status':
      return values.map((value) => choiceLabel(i18n, field.options, value)).join(', ');
    default:
      return values.join(', ');
  }
}

/**
 * Declara os filtros de uma lista uma vez só (P01, 06 §3.a): tipo, rótulo, schema de URL e
 * serialização para o fio. Da declaração saem o `validateSearch`, os parâmetros da API, os chips, a
 * contagem e o input da ação de filtrar; a `FilterBar` é gerada a partir dela.
 */
export function defineFilters<const Fs extends readonly FilterField[]>(fields: Fs): FilterSet<Fs> {
  const byKey = new Map<string, FilterField>();
  for (const field of fields) {
    for (const key of keysOf(field)) {
      if (byKey.has(key)) throw new Error(`defineFilters: chave repetida ${key}`);
      byKey.set(key, field);
    }
  }
  const keys = [...byKey.keys()];
  const shape = Object.assign({}, ...fields.map(urlSchemas)) as FilterShape<Fs>;
  const inputSchema = z.object(
    Object.assign({}, ...fields.map(inputSchemas)) as Record<string, z.ZodType>,
  ) as unknown as z.ZodType<FilterValues<Fs>>;

  const values = (search: Readonly<Record<string, unknown>>) => {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(search))
      if (byKey.has(key) && !blank(search[key])) out[key] = search[key];
    return out as FilterValues<Fs>;
  };

  const applied = (search: Readonly<Record<string, unknown>>) => {
    const list: AppliedFilter[] = [];
    for (const field of fields) {
      if (field.type === 'search') continue;
      const own = keysOf(field);
      const current = own.map((key) => search[key]);
      if (current.every(blank)) continue;
      const vals =
        (field.type === 'dateRange' || field.type === 'moneyRange')
          ? current.map((value) => (typeof value === 'string' ? value : ''))
          : asList(current[0]);
      list.push({
        field,
        values: vals,
        clear: { ...Object.fromEntries(own.map((key) => [key, undefined])), page: undefined },
      });
    }
    return list;
  };

  const set: FilterSet<Fs> = {
    fields,
    shape,
    inputSchema,
    keys,
    values,
    fieldOf: (key) => byKey.get(key),
    toParams: (search) => {
      const out: Record<string, unknown> = {};
      const done = new Set<FilterField>();
      for (const key of Object.keys(search)) {
        const field = byKey.get(key);
        if (!field || done.has(field) || blank(search[key])) continue;
        done.add(field);
        if (field.type === 'moneyRange') {
          const from = search[field.key];
          const to = search[field.to];
          if (typeof to === 'string' && to !== '' && field.sendTo !== false) {
            const value = (field.toParam ?? moneyCentsParam)(to);
            if (!blank(value)) out[field.toParamName ?? field.to] = value;
          }
          if (typeof from === 'string' && from !== '') {
            const value = (field.fromParam ?? moneyCentsParam)(from);
            if (!blank(value)) out[field.param ?? field.key] = value;
          }
          continue;
        }
        if (field.type === 'dateRange') {
          const from = search[field.key];
          const to = search[field.to];
          // Legado (`filterContext.tsx`, `toFiltersForUrl`): o fim entra antes do início.
          if (typeof to === 'string' && to !== '' && field.sendTo !== false)
            out[field.toParamName ?? field.to] = (field.toParam ?? endOfDayParam)(to);
          if (typeof from === 'string' && from !== '')
            out[field.param ?? field.key] = (field.fromParam ?? startOfDayParam)(from);
          continue;
        }
        const wire = wireOf(field, search[key]);
        if (!blank(wire)) out[field.param ?? field.key] = wire;
      }
      return out;
    },
    applied,
    count: (search) => applied(search).length,
    isActive: (search) => keys.some((key) => !blank(search[key])),
    patch: (next) => {
      const out: Record<string, unknown> = Object.fromEntries(keys.map((key) => [key, undefined]));
      for (const [key, value] of Object.entries(next))
        if (byKey.has(key) && !blank(value)) out[key] = value;
      out['page'] = undefined;
      return out;
    },
    clearPatch: () => ({
      ...(Object.fromEntries(keys.map((key) => [key, undefined])) as Record<string, undefined>),
      page: undefined,
    }),
    pick: (...only) =>
      defineFilters(fields.filter((field) => only.includes(field.key)) as unknown as Fs),
  };
  return set;
}

/** Qualquer conjunto de filtros (para quem só desenha). */
export type AnyFilterSet = FilterSet;
